// Account-scoped settings are cleared on explicit sign-out and restored from
// the server on sign-in. Unscoped reader and element defaults stay on the device.

import { resetLibraryAccess } from '@/lib/auth/libraryAccess';
import { LISTEN_POSITIONS_KEY, LISTEN_SETTINGS_KEY } from '@/lib/listen/settings';

/** Legacy unscoped listening settings and places, and the agent toggle, belong to the departing account. */
export const ACCOUNT_KEYS: readonly string[] = [LISTEN_SETTINGS_KEY, LISTEN_POSITIONS_KEY, 'knowsh:agent.enabled'];
export const ACCOUNT_KEY_PREFIXES = ['knowsh.dock.', 'know.plate.quiz.', 'knowsh:quiz-review-draft:'] as const;

type Reset = () => void;

const resets = new Set<Reset>();

// Accounts let go in this page. A feature still mounted for one can write its
// last state as it unmounts or as the page hides, so those writes are swept
// again: keys naming the account always, the unscoped per-account keys only
// until the next account signs in here and they become that account's.
const ended = new Set<string>();
let sweepUnscoped = false;
let sweepOnHide = false;

/** Runs `reset` whenever an account is let go; returns the unsubscribe. */
export function onAccountReset(reset: Reset): () => void {
  resets.add(reset);
  return () => {
    resets.delete(reset);
  };
}

export const isAccountKey = (key: string, subject: string): boolean =>
  (key.includes(subject) || key.includes(encodeURIComponent(subject))) || ACCOUNT_KEYS.includes(key) || ACCOUNT_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

function storages(): typeof window.localStorage[] {
  const found: typeof window.localStorage[] = [];
  for (const read of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      found.push(read());
    } catch {
      // Blocked storage has nothing of anyone's in it.
    }
  }
  return found;
}

function removeStored(match: (key: string) => boolean): void {
  for (const storage of storages()) {
    try {
      const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
      keys.filter((key): key is string => key !== null && match(key)).forEach((key) => storage.removeItem(key));
    } catch {
      // A storage that refuses to list or remove keeps what it has; the rest still go.
    }
  }
}

/**
 * Removes again whatever the accounts already let go have written since.
 * `unmounted` names the account whose tree just went: what it wrote to the
 * unscoped keys on the way out is its own, even once the next account is in.
 */
export function sweepEndedAccounts(unmounted?: string): void {
  if (ended.size === 0) return;
  const subjects = [...ended];
  const unscoped = sweepUnscoped || (unmounted !== undefined && ended.has(unmounted));
  removeStored((key) => subjects.some((subject) => (unscoped ? isAccountKey(key, subject) : (key.includes(subject) || key.includes(encodeURIComponent(subject))))));
}

/** An account signing in (again) in this page is no longer one to sweep away, and owns the unscoped keys from now on. */
export function resumeAccount(subject: string): void {
  ended.delete(subject);
  sweepUnscoped = false;
}

/**
 * Lets one account go: every registered in-memory reset runs (the query cache
 * is cancelled and cleared, module stores are emptied), then, unless
 * `keepStored`, every stored key of theirs is removed. `keepStored` is for a
 * session that ended without the reader choosing to leave: their settings wait
 * for them, and go only when a different account signs in.
 */
export function resetAccount(subject: string, { keepStored = false }: { keepStored?: boolean } = {}): void {
  resetLibraryAccess();
  for (const reset of [...resets]) {
    try {
      reset();
    } catch {
      // One feature failing to let go must not keep the others holding on.
    }
  }
  if (keepStored) return;
  ended.add(subject);
  sweepUnscoped = true;
  removeStored((key) => isAccountKey(key, subject));
  if (!sweepOnHide && typeof window !== 'undefined') {
    sweepOnHide = true;
    window.addEventListener('pagehide', () => sweepEndedAccounts());
  }
}
